import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCompany } from '../../Contexts/CompanyContext';
import { useBrands } from '../../Contexts/BrandContext';
import {
    Product,
    getProductDisplayInfo
} from '../../Services/productService';
import { Brand } from '../../Services/brandService';
import { useFeaturedProducts } from '../../Hooks/useProductQuery';

/* ================= ANIMATION STYLES ================= */
const featuredAnimStyles = `

  /* ── Card entrance ── */
  .fp-card {
    opacity: 0;
    transform: translateY(36px);
    transition:
      opacity 0.65s cubic-bezier(0.16, 1, 0.3, 1),
      transform 0.65s cubic-bezier(0.16, 1, 0.3, 1);
  }
  .fp-card.fp-card--visible {
    opacity: 1;
    transform: translateY(0);
  }

  /* Stagger per card position */
  .fp-card:nth-child(1)  { transition-delay: 0ms; }
  .fp-card:nth-child(2)  { transition-delay: 80ms; }
  .fp-card:nth-child(3)  { transition-delay: 160ms; }
  .fp-card:nth-child(4)  { transition-delay: 240ms; }
  .fp-card:nth-child(5)  { transition-delay: 320ms; }
  .fp-card:nth-child(6)  { transition-delay: 400ms; }
  .fp-card:nth-child(7)  { transition-delay: 480ms; }
  .fp-card:nth-child(8)  { transition-delay: 560ms; }
  .fp-card:nth-child(9)  { transition-delay: 640ms; }
  .fp-card:nth-child(10) { transition-delay: 720ms; }
  .fp-card:nth-child(11) { transition-delay: 800ms; }
  .fp-card:nth-child(12) { transition-delay: 880ms; }

  /* ── Product name hover underline ── */
  .fp-product-name {
    position: relative;
    display: inline-block;
  }
  .fp-product-name::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 0;
    height: 1px;
    background: #717fe0;
    transition: width 0.3s ease;
  }
  .fp-product-name:hover::after {
    width: 100%;
  }

  /* ── View All button entrance ── */
  .fp-viewall {
    opacity: 0;
    transform: translateY(16px);
    transition: opacity 0.6s ease, transform 0.6s ease;
  }
  .fp-viewall.fp-viewall--visible {
    opacity: 1;
    transform: translateY(0);
  }

  /* ── Sold out badge pulse ── */
  @keyframes fp-sold-pulse {
    0%, 100% { box-shadow: 0 2px 8px rgba(232, 64, 64, 0.35); }
    50%       { box-shadow: 0 2px 16px rgba(232, 64, 64, 0.6); }
  }
  .fp-sold-badge {
    animation: fp-sold-pulse 2.4s ease-in-out infinite;
  }

  /* ── Discount badge pop-in ── */
  @keyframes fp-badge-pop {
    0%   { transform: scale(0.5); opacity: 0; }
    70%  { transform: scale(1.15); }
    100% { transform: scale(1); opacity: 1; }
  }
  .fp-discount-badge {
    animation: fp-badge-pop 0.4s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
  }

  /* ── Brand name shimmer on card hover ── */
  .fp-brand {
    position: relative;
    overflow: hidden;
  }
  .fp-brand::after {
    content: '';
    position: absolute;
    top: 0; left: -100%;
    width: 60%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(113,127,224,0.25), transparent);
    transition: left 0.5s ease;
  }
  .fp-card:hover .fp-brand::after {
    left: 150%;
  }
`;

/* ================= IMAGE COMPONENT ================= */

interface ProductImageProps {
    src?: string;
    alt: string;
}

const ProductImageWithPlaceholder: React.FC<ProductImageProps> = ({ src, alt }) => {
    const [loaded, setLoaded] = useState(false);
    const imageSrc = src || 'asset/images/product-placeholder.png';

    return (
        <>
            {!loaded && (
                <img
                    src="asset/images/product-placeholder.png"
                    alt="placeholder"
                    style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
                />
            )}
            <img
                src={imageSrc}
                alt={alt}
                style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', display: loaded ? 'block' : 'none' }}
                onLoad={() => setLoaded(true)}
            />
        </>
    );
};

/* ================= PRODUCT CARD COMPONENT ================= */

interface ProductCardProps {
    product: Product;
    formatPrice: (price: number) => string;
    onProductClick: (id: string) => void;
    brands: Brand[];
}

const ProductCard: React.FC<ProductCardProps> = ({ product, formatPrice, onProductClick, brands }) => {
    const [hovered, setHovered] = useState(false);
    const displayInfo = getProductDisplayInfo(product);
    const isSoldOut = displayInfo.isOutOfStock;

    const brandName = product.brandId
        ? brands.find(b => b.id === product.brandId)?.name ?? null
        : null;

    return (
        /* fp-card class drives the IntersectionObserver entrance animation */
        <div className="col-sm-6 col-md-4 col-lg-3 p-b-35 isotope-item fp-card">
            <div className="block2">
                <div
                    className="block2-pic"
                    style={{
                        position: 'relative',
                        width: '100%',
                        paddingBottom: '125%',
                        overflow: 'hidden',
                        backgroundColor: '#f8f9fa'
                    }}
                    onMouseEnter={() => setHovered(true)}
                    onMouseLeave={() => setHovered(false)}
                >
                    {/* Image wrapper — zoom on hover */}
                    <div
                        style={{
                            position: 'absolute',
                            inset: 0,
                            transform: hovered ? 'scale(1.1)' : 'scale(1)',
                            transition: 'transform 0.4s ease',
                            willChange: 'transform',
                        }}
                    >
                        <ProductImageWithPlaceholder
                            src={product.images?.[0]}
                            alt={product.name}
                        />
                    </div>

                    {/* Dark overlay on hover */}
                    <div
                        style={{
                            position: 'absolute',
                            inset: 0,
                            backgroundColor: 'rgba(0, 0, 0, 0.2)',
                            opacity: hovered ? 1 : 0,
                            transition: 'opacity 0.4s ease',
                            zIndex: 1,
                            pointerEvents: 'none',
                        }}
                    />

                    {/* Sold Out pill badge */}
                    {isSoldOut && (
                        <div
                            className="fp-sold-badge"
                            style={{
                                position: 'absolute',
                                top: '12px',
                                left: '12px',
                                zIndex: 5,
                                background: '#e84040',
                                color: '#fff',
                                fontSize: '11px',
                                fontWeight: 800,
                                letterSpacing: '1.2px',
                                padding: '5px 13px',
                                borderRadius: '30px',
                                textTransform: 'uppercase',
                                pointerEvents: 'none',
                                boxShadow: '0 2px 8px rgba(232, 64, 64, 0.35)',
                            }}
                        >
                            SOLD OUT
                        </div>
                    )}

                    {/* View Product button — slides up on hover */}
                    <div
                        style={{
                            position: 'absolute',
                            bottom: 0,
                            left: 0,
                            width: '100%',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            padding: '0 80px 20px 80px',
                            opacity: hovered ? 1 : 0,
                            transform: hovered ? 'translateY(0px)' : 'translateY(20px)',
                            transition: 'opacity 0.4s ease, transform 0.4s ease',
                            zIndex: 2,
                            boxSizing: 'border-box',
                            pointerEvents: hovered ? 'auto' : 'none',
                        }}
                    >
                        <a
                            href="#"
                            className="flex-c-m stext-103 cl2 size-102 bg0 bor2 hov-btn1 p-lr-15 trans-04"
                            style={{ width: '100%' }}
                            onClick={(e) => { e.preventDefault(); onProductClick(product.id); }}
                        >
                            View Product
                        </a>
                    </div>
                </div>

                {/* Product info */}
                <div className="block2-txt flex-w flex-t p-t-14">
                    <div className="block2-txt-child1 flex-col-l" style={{ width: '100%' }}>

                        {/* Name — fp-product-name for underline animation */}
                        <a
                            href="#"
                            className="fp-product-name stext-104 cl4 hov-cl1 trans-04 p-b-6"
                            onClick={(e) => { e.preventDefault(); onProductClick(product.id); }}
                        >
                            {product.name}
                        </a>

                        {/* Price */}
                        <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '2px' }}>
                            {displayInfo.hasDiscount ? (
                                <>
                                    <span className="stext-105 cl3" style={{ fontWeight: 600 }}>
                                        {formatPrice(displayInfo.discountedPrice)}
                                    </span>
                                    <span style={{ textDecoration: 'line-through', color: '#aaa', fontSize: '13px', marginLeft: '6px', fontWeight: 400 }}>
                                        {formatPrice(displayInfo.price)}
                                    </span>
                                    {/* fp-discount-badge for pop-in animation */}
                                    <span
                                        className="fp-discount-badge"
                                        style={{
                                            display: 'inline-block',
                                            background: '#e74c3c',
                                            color: '#fff',
                                            fontSize: '10px',
                                            fontWeight: 700,
                                            letterSpacing: '0.8px',
                                            padding: '2px 7px',
                                            borderRadius: '2px',
                                            marginLeft: '6px',
                                            verticalAlign: 'middle',
                                            lineHeight: 1.6,
                                        }}>
                                        -{displayInfo.discount}%
                                    </span>
                                </>
                            ) : (
                                <span className="stext-105 cl3">
                                    {formatPrice(displayInfo.price)}
                                </span>
                            )}
                        </div>

                        {/* Brand — fp-brand for shimmer animation */}
                        {brandName && (
                            <span
                                className="fp-brand"
                                style={{
                                    display: 'block',
                                    marginTop: '5px',
                                    fontSize: '10px',
                                    fontWeight: 700,
                                    letterSpacing: '1.4px',
                                    textTransform: 'uppercase',
                                    color: '#717fe0',
                                    lineHeight: 1.4,
                                }}>
                                {brandName}
                            </span>
                        )}

                        {/* Variant count */}
                        {product.hasVariants && product.variants && product.variants.length > 1 && (
                            <span className="stext-107 cl6" style={{ fontSize: '12px', marginTop: '4px' }}>
                                {product.variants.length} variants available
                            </span>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

/* ================= MAIN COMPONENT ================= */

const FeaturedProductsShop: React.FC = () => {
    const navigate = useNavigate();
    const { formatPrice } = useCompany();
    const { allBrands } = useBrands();

    // React Query replaces manual useEffect + local products/loading state.
    // useFeaturedProducts caches the result for 5 minutes, so navigating back
    // to this page within that window costs zero Firestore reads.
    const { data: products = [], isLoading } = useFeaturedProducts();

    const gridRef = useRef<HTMLDivElement>(null);
    const viewAllRef = useRef<HTMLDivElement>(null);

    // ── IntersectionObserver: staggered card entrance ─────────────────────────
    //
    // CACHE-HIT PROBLEM: When data comes from cache, `isLoading` is never true,
    // so the component goes straight to the render path. The cards are stamped
    // into the DOM with class `fp-card` (opacity: 0) but no observer is watching
    // them yet. We solve this by running the observer setup inside a useEffect
    // that depends on `products` — it fires whether data arrived from the network
    // OR from the cache, always after the DOM has been painted with the new cards.
    //
    // We also strip fp-card--visible from every card before re-observing so that
    // each fresh mount replays the entrance animation even on a cache hit.
    useEffect(() => {
        if (!gridRef.current || products.length === 0) return;

        const cards = gridRef.current.querySelectorAll<HTMLElement>('.fp-card');

        // Reset visibility so the animation replays on every mount, including
        // when React Query serves the result from cache with no loading phase.
        cards.forEach(card => card.classList.remove('fp-card--visible'));

        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        entry.target.classList.add('fp-card--visible');
                        observer.unobserve(entry.target);
                    }
                });
            },
            { threshold: 0.08, rootMargin: '0px 0px -40px 0px' }
        );

        cards.forEach((card) => observer.observe(card));
        return () => observer.disconnect();
    }, [products]); // re-runs whenever products array reference changes (network OR cache)

    // ── IntersectionObserver: View All button entrance ────────────────────────
    // Same pattern — depends on products so it re-registers after every mount.
    useEffect(() => {
        const el = viewAllRef.current;
        if (!el || products.length === 0) return;

        // Reset so it re-animates on every fresh mount
        el.classList.remove('fp-viewall--visible');

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    el.classList.add('fp-viewall--visible');
                    observer.disconnect();
                }
            },
            { threshold: 0.5 }
        );

        observer.observe(el);
        return () => observer.disconnect();
    }, [products]);

    const handleProductClick = (productId: string) => {
        navigate(`/product/${productId}`);
    };

    const handleViewAllClick = () => {
        navigate('/shop');
    };

    /* ── Loading state ── */
    if (isLoading) {
        return (
            <div className="bg0 m-t-23 p-b-0">
                <div className="container">
                    <div className="flex-c-m w-full p-t-45">
                        <span className="stext-101 cl5">Loading featured products...</span>
                    </div>
                </div>
            </div>
        );
    }

    if (products.length === 0) {
        return null;
    }

    return (
        <>
            <style>{featuredAnimStyles}</style>

            <div className="bg0 m-t-23 p-b-0">
                <div className="container">

                    {/* ================= PRODUCTS GRID ================= */}
                    <div className="row isotope-grid" ref={gridRef}>
                        {products.map(product => (
                            <ProductCard
                                key={product.id}
                                product={product}
                                formatPrice={formatPrice}
                                onProductClick={handleProductClick}
                                brands={allBrands || []}
                            />
                        ))}
                    </div>

                    {/* ================= VIEW ALL BUTTON ================= */}
                    <div
                        ref={viewAllRef}
                        className="fp-viewall flex-c-m flex-w w-full p-t-10 p-b-45"
                    >
                        <a
                            href="#"
                            className="flex-c-m stext-101 cl5 size-103 bg2 bor1 hov-btn1 p-lr-15 trans-04"
                            onClick={(e) => { e.preventDefault(); handleViewAllClick(); }}
                        >
                            View All Products
                        </a>
                    </div>

                </div>
            </div>
        </>
    );
};

export default FeaturedProductsShop;